import type { DesignCanvas, Layer, ImageLayer, TextLayer } from '../document/types.ts'
import { MM_PER_PT } from '../units.ts'
import { hexagonPoints, shapePath } from '../geometry/shapes.ts'
import { computeImagePlacement } from '../layout/imagePlacement.ts'

/**
 * Die einzige Stelle, an der Layout-Regeln in CSS übersetzt werden. Vom Vue-Surface (`:style`)
 * und von renderToHtml (`style="…"`) gleichermaßen benutzt. Längen als calc(var(--mm)*N).
 */
export type StyleMap = Record<string, string>

export function mm(value: number): string {
  return `calc(var(--mm) * ${fmt(value)})`
}

function fmt(n: number): string {
  return Number.isFinite(n) ? String(Math.round(n * 10000) / 10000) : '0'
}

export function rootStyle(canvas: DesignCanvas, pxPerMm: number): StyleMap {
  const fullW = canvas.widthMm + 2 * canvas.bleedMm
  const fullH = canvas.heightMm + 2 * canvas.bleedMm
  return {
    '--mm': `${fmt(pxPerMm)}px`,
    width: mm(fullW),
    height: mm(fullH),
  }
}

/** clip-path der Produktform auf der Trim-Box (Produktansicht/Vorschau, nie im Druck). */
export function productClipPath(canvas: DesignCanvas): string {
  const b = canvas.bleedMm
  const W = canvas.widthMm
  const H = canvas.heightMm
  if (canvas.shape === 'circle') return `ellipse(${mm(W / 2)} ${mm(H / 2)} at 50% 50%)`
  if (canvas.shape === 'hexagon') {
    const pts = hexagonPoints({ x: b, y: b, w: W, h: H }, canvas.shapeParams?.orientation === 'pointy' ? 'pointy' : 'flat')
    return `polygon(${pts.map((p) => `${mm(p.x)} ${mm(p.y)}`).join(', ')})`
  }
  if (canvas.shape === 'rounded' && canvas.cornerRadiusMm > 0) return `inset(${mm(b)} round ${mm(canvas.cornerRadiusMm)})`
  return `inset(${mm(b)})`
}

function baseLayerStyle(layer: Layer, canvas: DesignCanvas): StyleMap {
  const f = layer.frame
  const style: StyleMap = {
    left: mm(f.xMm + canvas.bleedMm),
    top: mm(f.yMm + canvas.bleedMm),
    width: mm(f.wMm),
    height: mm(f.hMm),
    opacity: fmt(layer.opacity),
  }
  if (f.rotationDeg) style.transform = `rotate(${fmt(f.rotationDeg)}deg)`
  return style
}

export function layerStyle(layer: Layer, canvas: DesignCanvas): StyleMap {
  return baseLayerStyle(layer, canvas)
}

/** Maske eines Bildlayers als clip-path (Basisformen in % => skaliert mit --mm). */
export function imageMaskClipPath(layer: ImageLayer, maskDefId?: string): string | null {
  const m = layer.mask
  if (m.kind === 'circle') return 'ellipse(50% 50% at 50% 50%)'
  if (m.kind === 'rounded') return `inset(0 round ${mm(m.radiusMm)})`
  if (m.kind === 'hexagon') {
    const pts = hexagonPoints({ x: 0, y: 0, w: 100, h: 100 }, m.orientation ?? 'flat')
    return `polygon(${pts.map((p) => `${fmt(p.x)}% ${fmt(p.y)}%`).join(', ')})`
  }
  if ((m.kind === 'svg' || m.kind === 'asset') && maskDefId) return `url(#${maskDefId})`
  return null
}

export function imageClipStyle(layer: ImageLayer, maskDefId?: string): StyleMap {
  const style: StyleMap = {}
  const clip = imageMaskClipPath(layer, maskDefId)
  if (clip) style.clipPath = clip
  if (layer.border && layer.border.widthMm > 0) {
    style.border = `${mm(layer.border.widthMm)} solid ${layer.border.color}`
  }
  if (layer.shadow) {
    style.boxShadow = `${mm(layer.shadow.xMm)} ${mm(layer.shadow.yMm)} ${mm(layer.shadow.blurMm)} ${layer.shadow.color}`
  }
  return style
}

export function imageFilterCss(layer: ImageLayer): string {
  const f = layer.filters
  const parts: string[] = []
  if (f.grayscale) parts.push('grayscale(1)')
  if (f.sepia > 0) parts.push(`sepia(${fmt(f.sepia / 100)})`)
  if (f.brightness) parts.push(`brightness(${fmt(1 + f.brightness / 100)})`)
  if (f.contrast) parts.push(`contrast(${fmt(1 + f.contrast / 100)})`)
  if (f.saturation) parts.push(`saturate(${fmt(1 + f.saturation / 100)})`)
  return parts.join(' ')
}

export function imageElementStyle(layer: ImageLayer, source: { width: number; height: number }): StyleMap {
  const inner = layer.border && layer.border.widthMm > 0 ? layer.border.widthMm : 0
  const frame = { wMm: Math.max(0, layer.frame.wMm - 2 * inner), hMm: Math.max(0, layer.frame.hMm - 2 * inner) }
  const p = computeImagePlacement(frame, source, layer.fit)
  const style: StyleMap = {
    left: mm(p.xMm),
    top: mm(p.yMm),
    width: mm(p.wMm),
    height: mm(p.hMm),
  }
  const filter = imageFilterCss(layer)
  if (filter) style.filter = filter
  const flips: string[] = []
  if (layer.fit.flipH) flips.push('scaleX(-1)')
  if (layer.fit.flipV) flips.push('scaleY(-1)')
  if (flips.length) style.transform = flips.join(' ')
  return style
}

export function textStyle(layer: TextLayer): StyleMap {
  const sizePt = layer.autoFit === 'shrink' && layer.fittedSizePt ? layer.fittedSizePt : layer.font.sizePt
  const family = layer.font.family || layer.font.familyId || 'sans-serif'
  const style: StyleMap = {
    fontFamily: `"${family.replace(/["\\]/g, '\\$&')}"`,
    fontWeight: String(layer.font.weight),
    fontStyle: layer.font.italic ? 'italic' : 'normal',
    fontSize: mm(sizePt * MM_PER_PT),
    lineHeight: fmt(layer.font.lineHeight),
    letterSpacing: `${fmt(layer.font.letterSpacingEm)}em`,
    color: layer.color,
    textAlign: layer.align,
    justifyContent: layer.verticalAlign === 'middle' ? 'center' : layer.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
  }
  if (layer.stroke && layer.stroke.widthMm > 0) {
    style.webkitTextStroke = `${mm(layer.stroke.widthMm)} ${layer.stroke.color}`
    style.paintOrder = 'stroke fill'
  }
  if (layer.shadow) {
    style.textShadow = `${mm(layer.shadow.xMm)} ${mm(layer.shadow.yMm)} ${mm(layer.shadow.blurMm)} ${layer.shadow.color}`
  }
  return style
}

/** SVG-Markup eines Shape-Layers (viewBox in mm, skaliert mit dem Rahmen). */
export function shapeSvgInner(layer: Extract<Layer, { type: 'shape' }>): { viewBox: string; body: string } {
  const w = Math.max(0.01, layer.frame.wMm)
  const h = Math.max(0.01, layer.frame.hMm)
  const stroke = layer.stroke && layer.stroke.widthMm > 0
    ? ` stroke="${layer.stroke.color}" stroke-width="${fmt(layer.stroke.widthMm)}"${layer.dashed ? ` stroke-dasharray="${fmt(layer.stroke.widthMm * 3)} ${fmt(layer.stroke.widthMm * 2)}"` : ''}`
    : ' stroke="none"'
  const sw = layer.stroke && layer.stroke.widthMm > 0 ? layer.stroke.widthMm : 0
  const inset = sw / 2
  const rect = { x: inset, y: inset, w: Math.max(0, w - sw), h: Math.max(0, h - sw) }
  let body: string
  if (layer.shape === 'line') {
    body = `<line x1="0" y1="${fmt(h / 2)}" x2="${fmt(w)}" y2="${fmt(h / 2)}" fill="none"${stroke || ` stroke="${layer.fill}" stroke-width="${fmt(h)}"`}/>`
  } else if (layer.shape === 'ellipse') {
    body = `<path d="${shapePath({ shape: 'circle' }, rect)}" fill="${layer.fill}"${stroke}/>`
  } else if (layer.shape === 'polygon') {
    body = `<path d="${regularPolygonPath(layer.sides ?? 6, rect)}" fill="${layer.fill}"${stroke}/>`
  } else {
    body = `<path d="${shapePath({ shape: layer.cornerRadiusMm > 0 ? 'rounded' : 'rect', cornerRadius: layer.cornerRadiusMm }, rect)}" fill="${layer.fill}"${stroke}/>`
  }
  return { viewBox: `0 0 ${fmt(w)} ${fmt(h)}`, body }
}

function regularPolygonPath(sides: number, r: { x: number; y: number; w: number; h: number }): string {
  const n = Math.max(3, Math.round(sides))
  const cx = r.x + r.w / 2
  const cy = r.y + r.h / 2
  const rx = r.w / 2
  const ry = r.h / 2
  const pts: string[] = []
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n
    pts.push(`${i === 0 ? 'M' : 'L'}${fmt(cx + rx * Math.cos(a))} ${fmt(cy + ry * Math.sin(a))}`)
  }
  return pts.join(' ') + ' Z'
}

export function styleMapToCss(style: StyleMap): string {
  return Object.entries(style)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${toKebab(k)}:${v}`)
    .join(';')
}

function toKebab(key: string): string {
  if (key.startsWith('--')) return key
  if (key.startsWith('webkit')) return '-webkit-' + key.slice(6).replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())
  return key.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())
}
